import { useState } from 'react';
import type { Role } from '../services/roleApi';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoreHorizontal, Search, Shield, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeleteRole } from '../hooks/useRoles';
import { EditPermissionsDialog } from './EditPermissionsDialog';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { useConfirm, withConfirmSuccess } from '@/shared/contexts/confirm-context';

interface RolesTableProps {
  roles: Role[];
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export const RolesTable = ({
  roles,
  searchTerm,
  onSearchTermChange,
  currentPage,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: RolesTableProps) => {
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const deleteMutation = useDeleteRole();
  const confirm = useConfirm();

  const handleDelete = (id: string, name: string) => {
    confirm({
      title: 'Delete role?',
      description: `The role “${name}” will be permanently removed. Users assigned to it will lose its permissions. This can't be undone.`,
      variant: 'destructive',
      confirmLabel: 'Delete',
      onConfirm: () =>
        withConfirmSuccess(deleteMutation.mutateAsync(id), `${name} deleted`),
    });
  };

  return (
    <div className="space-y-4">
      <div className="relative w-64 ml-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search roles..."
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell className="font-medium align-top">{role.name}</TableCell>
                <TableCell className="whitespace-normal break-words max-w-xs align-top">{role.description}</TableCell>
                <TableCell className="whitespace-normal align-top">
                  <div className="flex flex-wrap gap-1">
                    {role.permissions.length === 0 ? (
                      <span className="text-gray-500 text-xs">
                        No permissions
                      </span>
                    ) : (
                      <>
                        {role.permissions.slice(0, 3).map((perm) => (
                          <Badge key={perm} variant="outline" className="text-xs">
                            {perm}
                          </Badge>
                        ))}
                        {role.permissions.length > 3 && (
                          <Badge key="more-permissions" variant="outline" className="text-xs">
                            +{role.permissions.length - 3}
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onSelect={() => setEditingRole(role)}>
                        <Shield className="w-4 h-4 mr-2" />
                        Manage Permissions
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDelete(role.id, role.name)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {roles.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No roles found
        </div>
      ) : (
        <PaginationBar
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}

      {editingRole && (
        <EditPermissionsDialog
          role={editingRole}
          open={!!editingRole}
          onOpenChange={(open) => {
            if (!open) setEditingRole(null);
          }}
        />
      )}
    </div>
  );
};
